import * as StructureActions from "../actions/Structure.Actions";
import { IStructure, IUser, IAircraftLastStatus, IAirportGate, DomainArea } from "@vmosolutions/oc-interfaces";

export interface IAirportOption {
  [x: string]: any;
  _id: string | undefined;
  ident: string;
  name: string;
  timezoneId: string;
  iata: string | null;
  gates?: IAirportGate[];
}

export interface IAirportOptionMap {
  [key: string]: IAirportOption;
}

export interface IAirportSearchMap {
  [key: string]: boolean;
}

export interface IAircraftStatusMap {
  [key: string]: IAircraftLastStatus;
}
export interface IStructuresState {
  customerStructure: IStructure;
  airportOptions: IAirportOptionMap;
  appUsers: IUser[];
  aircraftStatus: IAircraftStatusMap;
}

const initialState: IStructuresState = {
  customerStructure: {
    ATAs: [],
    aircraft: [],
    aircraftGroups: [],
    customActions: [],
    delayCategories: [],
    flightFlags: [],
    flightTypes: [],
    nonFlyingTypes: [],
    customers: [],
    diversionReasons: [],
    operators: [],
    positions: [],
    crewBases: [],
    crewRuleCategories: [],
    globalAppConfig: {
      active: true,
      environment: undefined,
      customer: {
        primaryAirlineCode: "",
        customerFullName: "",
        customerPrimaryBase: "",
        iataCode: "",
        settings: {
          defaultFlightTypeId: null,
          defaultGroundEventTypeId: null,
          defaultTaxiOutMinutes: 5,
          defaultTaxiInMinutes: 5,
          isAPISEnabled: false,
          defaultCrewCheckInMinutes: 0,
          delayBufferDeparture: null,
          delayBufferArrival: null,
          defaultFlightReleaseWarningHours: 0,
          defaultFlightReleaseErrorHours: 0,
          defaultLongFlightDurationHours: 0,
          crewScheduleEmails: [],
          overdueDepartureMovementMsgMinutes: 30,
          overdueArrivalMovementMsgMinutes: 30,
          defaultFlightTimelineLoadDays: 16,
        },
      },
      reports: { indexes: [] },
      appStage: "",
      region: "",
      userInboxBucket: "",
      themeDefaults: undefined,
      domains: new Map<DomainArea, string>(),
    },
  },
  airportOptions: {}, // Populated with list of commonly used airports (on login) followed by addition of any searched airport during session.
  appUsers: [],
  aircraftStatus: {}, // Populated with a list of last known aircraft statuses.
};

export const StructuresReducer = (
  state: IStructuresState = initialState,
  action: StructureActions.Action
): IStructuresState => {
  switch (action.type) {
    case StructureActions.Type.GOT_AIRPORTS: {
      const airportOptions = { ...state.airportOptions };
      action.airportOptions.forEach((item: IAirportOption) => {
        airportOptions[item.ident] = item;
      });
      return { ...state, airportOptions };
    }
    case StructureActions.Type.GOT_STRUCTURES:
      return { ...state, customerStructure: { ...action.customerStructures } };
    case StructureActions.Type.GOT_APP_USERS:
      return { ...state, appUsers: [...action.appUsers] };
    case StructureActions.Type.SORT_AIRCRAFT:
      return {
        ...state,
        customerStructure: {
          ...state.customerStructure,
          aircraft: [...action.aircraft],
        },
      };
    case StructureActions.Type.GOT_AIRCRAFT_STATUS:
      if (action?.updateAircraftId) {
        // If updateAircraftId is passed in, it's likely a websocket update so only modify that aircraft's status.
        const updatedStatusMap = { ...state.aircraftStatus };
        updatedStatusMap[action?.updateAircraftId] = action.aircraftStatus[action?.updateAircraftId];
        return { ...state, aircraftStatus: updatedStatusMap };
      }
      return { ...state, aircraftStatus: { ...action.aircraftStatus } };
    case StructureActions.Type.GOT_AIRCRAFT_MAINTENANCE_UPDATE: {
      const idx = state.customerStructure.aircraft.findIndex((ac) => ac.registration === action.registration);
      if (idx === -1) return state;

      const updatedAircraft = [...state.customerStructure.aircraft];
      const { maintenance } = action;
      updatedAircraft[idx] = { ...updatedAircraft[idx], maintenance };
      const customerStructure = { ...state.customerStructure, aircraft: updatedAircraft };

      return {
        ...state,
        customerStructure,
      };
    }
    default:
      return state;
  }
};
